import React, { useState, useEffect, useRef } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, Link, Tabs, Tab, Collapse, IconButton, Button, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Box, Divider } from '@mui/material';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, StarBorder as StarBorderIcon, Star as StarIcon, Close as CloseIcon, Event as EventIcon, Place as PlaceIcon, Schedule as ScheduleIcon, SportsBaseball as CourtIcon, OpenInNew as OpenInNewIcon, CallMade as CallMadeIcon, Phone as PhoneIcon, Google as GoogleIcon, Apple as AppleIcon } from '@mui/icons-material';
import { logEvent } from 'firebase/analytics';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import 'firebase/compat/firestore';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { canAddBookingsToCalendar } from '../utils/accountFeatures';

const StyledTabs = styled(Tabs)({
  '& .MuiTab-root': {
    color: 'var(--cc-text-soft)',
    opacity: 1,
    fontWeight: 700,
    textTransform: 'none',
    '&.Mui-selected': {
      color: 'var(--cc-accent-dark)'
    }
  },
  '& .MuiTabs-indicator': {
    backgroundColor: 'var(--cc-accent)',
    height: 3,
    borderRadius: '3px 3px 0 0'
  }
});

const times = ["09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00", "17:00", "18:00", "19:00", "20:00", "21:00", "22:00"];

const formatDate = (dateString, isFrench, isMobile) => {
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  
  const weekdays = {
    en: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    fr: ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi']
  };
  const months = {
    en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    fr: ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc']
  };
  
  const weekday = weekdays[isFrench ? 'fr' : 'en'][date.getDay()];
  const monthName = months[isFrench ? 'fr' : 'en'][date.getMonth()];
  
  return isMobile ? 
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: '1.2' }}>
      <div>{weekday}</div>
      <div>{monthName} {day}</div>
    </div> :
    `${weekday}, ${monthName} ${day}`;
};

const formatFullDate = (dateString, isFrench) => {
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  
  const weekdays = {
    en: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    fr: ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi']
  };
  const months = {
    en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
    fr: ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre']
  };
  
  const weekday = weekdays[isFrench ? 'fr' : 'en'][date.getDay()];
  const monthName = months[isFrench ? 'fr' : 'en'][date.getMonth()];
  
  return isFrench
    ? `${weekday} ${day} ${monthName} ${year}`
    : `${weekday}, ${monthName} ${day}, ${year}`;
};

const GreenOutlinedButton = styled(Button)(({ theme }) => ({
  padding: '10px 16px',
  fontSize: '0.95rem',
  whiteSpace: 'nowrap',
  textTransform: 'none',
  color: 'var(--cc-accent-dark)',
  borderColor: 'var(--cc-accent)',
  fontWeight: 500,
  '&:hover': {
    borderColor: 'var(--cc-accent-dark)',
    backgroundColor: 'var(--cc-hover)'
  }
}));

const BlueOutlinedButton = styled(Button)(({ theme }) => ({
  padding: '10px 16px',
  fontSize: '0.95rem',
  whiteSpace: 'nowrap',
  textTransform: 'none',
  color: 'var(--cc-accent-dark)',
  borderColor: 'var(--cc-accent)',
  fontWeight: 500,
  '&:hover': {
    borderColor: 'var(--cc-accent-dark)',
    backgroundColor: 'var(--cc-hover)'
  }
}));

const GreenContainedButton = styled(Button)(({ theme }) => ({
  padding: '10px 16px',
  fontSize: '0.95rem',
  whiteSpace: 'nowrap',
  textTransform: 'none',
  backgroundColor: 'var(--cc-accent-strong)',
  color: 'var(--cc-on-accent)',
  fontWeight: 500,
  '&:hover': {
    backgroundColor: 'var(--cc-accent-dark)'
  },
  '&.Mui-disabled': {
    backgroundColor: 'var(--cc-surface-muted)',
    color: 'var(--cc-text-soft)'
  }
}));

const CalendarButton = styled(Button)(({ theme }) => ({
  padding: '8px 16px',
  fontSize: '0.9rem',
  textTransform: 'none',
  height: '40px',
  width: '100%',
  marginBottom: '8px',
  '&:hover': {
    backgroundColor: theme.palette.action.hover
  },
  '&.Mui-disabled': {
    backgroundColor: 'var(--cc-surface-muted)',
    color: 'var(--cc-text-soft)'
  }
}));

const translations = {
    en: {
        bookingLimitUnauth: "Create an account",
        bookingLimitUnauthDesc: "to get more bookings",
        bookingLimitAuth: "Upgrade to Premium plan for unlimited bookings",
        bookingsLeft: "bookings left",
        bookingsLeftWeek: "bookings left this week",
        calendarAccountText: "Create a free account to add events to your calendar",
        calendarAccountTitle: "Save this booking to your calendar",
        calendarAccountBody: "Create a free CourtClic account to add bookings to Google or Apple Calendar.",
        createAccount: "Create free account",
        signIn: "Sign in",
        predictiveTitle: "Predictive link (Premium)",
        predictiveNotice: "This time slot is predicted from past booking patterns. The court isn't released for booking yet, so availability isn't guaranteed.",
        predictiveUpgradeTitle: "Premium-only predictive link",
        predictiveUpgradeBody: "Predictive links open booking pages before the city officially releases them, giving Premium members a head start. Upgrade to unlock these slots.",
        availabilityLoading: "Loading availability…",
        upgrade: "Upgrade",
        close: "Close"
    },
    fr: {
        bookingLimitUnauth: "Créez un compte",
        bookingLimitUnauthDesc: "pour obtenir plus de réservations",
        bookingLimitAuth: "Passez au plan Premium pour des réservations illimitées",
        bookingsLeft: (count) => count <= 1 ? "réservation restante" : "réservations restantes",
        bookingsLeftWeek: (count) => count <= 1 ? "réservation restante cette semaine" : "réservations restantes cette semaine",
        calendarAccountText: "Créez un compte gratuit pour ajouter des événements à votre calendrier",
        calendarAccountTitle: "Ajoutez cette réservation à votre calendrier",
        calendarAccountBody: "Créez un compte CourtClic gratuit pour ajouter vos réservations à Google ou Apple Calendar.",
        createAccount: "Créer un compte gratuit",
        signIn: "Se connecter",
        predictiveTitle: "Lien prédictif (Premium)",
        predictiveNotice: "Ce créneau est prédit à partir des réservations passées. Le terrain n'est pas encore ouvert à la réservation, la disponibilité n'est pas garantie.",
        predictiveUpgradeTitle: "Lien prédictif réservé aux membres Premium",
        predictiveUpgradeBody: "Les liens prédictifs ouvrent les pages de réservation avant la mise en ligne officielle, ce qui donne une longueur d'avance aux membres Premium. Passez à Premium pour débloquer ces créneaux.",
        availabilityLoading: "Chargement des disponibilités…",
        upgrade: "Mettre à niveau",
        close: "Fermer"
    }
};

// Compare current Toronto wall-clock time against an unzoned ET ISO string
// (e.g. "2026-05-07T10:00:00"). Both sides are formatted as the same kind of
// naive local timestamp so a string compare is safe.
const getTorontoNowString = () => {
    if (typeof window !== 'undefined' && window.__predictiveNowOverride) {
        return window.__predictiveNowOverride;
    }
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/Toronto',
        hour12: false,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
    }).formatToParts(new Date()).reduce((acc, p) => {
        if (p.type !== 'literal') acc[p.type] = p.value;
        return acc;
    }, {});
    let hour = parts.hour;
    if (hour === '24') hour = '00';
    return `${parts.year}-${parts.month}-${parts.day}T${hour}:${parts.minute}:${parts.second}`;
};

const isPredictiveSlotVisible = (slot) => {
    const showAt = slot?.predictiveDisplay?.showAt;
    if (!showAt) return true;
    return getTorontoNowString() >= showAt;
};

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  paddingBottom: theme.spacing(1),
  borderBottom: '1px solid var(--cc-border)',
  '& .MuiTypography-root': {
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.1rem'
    }
  }
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  }
}));

const CourtAvailabilityTable = ({ 
  data, 
  isFrench, 
  analytics, 
  setFavorites, 
  prevFavorites, 
  setSelectedTab, 
  isExpanded, 
  onExpansionChange,
  selectedDate: parentSelectedDate,
  onDateChange,
  accountType: effectiveAccountType,
  styleguideLinks
}) => {
  const [selectedDate, setSelectedDate] = useState(
    parentSelectedDate || 
    (data.tableStats ? Object.keys(data.tableStats[0].availability).sort()[0] : [])
  );
  const [isMobile, setIsMobile] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [bookingBehavior, setBookingBehavior] = useState('search');
  const [directBookingsLeft, setDirectBookingsLeft] = useState(3);
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [showCalendarAccountPrompt, setShowCalendarAccountPrompt] = useState(false);
  const [selectedBookingDetails, setSelectedBookingDetails] = useState(null);
  const [weeklyBookingsLeft, setWeeklyBookingsLeft] = useState(null);
  const [quickLinks, setQuickLinks] = useState(styleguideLinks?.quickLinks || null);
  const [quickLinksLoaded, setQuickLinksLoaded] = useState(Boolean(styleguideLinks));
  const [predictiveLinks, setPredictiveLinks] = useState(styleguideLinks?.predictiveLinks || null);
  const [showPredictiveUpgrade, setShowPredictiveUpgrade] = useState(false);
  const navigate = useNavigate();
  const userIsPremium = Boolean(effectiveAccountType) && effectiveAccountType !== 'free';

  const handleResize = () => {
    const isMobile = window.innerWidth < 600;
    setIsMobile(isMobile);
  };

  useEffect(() => {
    const user = firebase.auth().currentUser;
    setCurrentUser(user);
  
    if (user) {
      // Get favorites
      const docRef = firebase.firestore().collection('userFavorites').doc(user.uid);
      docRef.get().then((doc) => {
        if (doc.exists && doc.data().favorites.includes(data.siteId)) {
          setIsFavorite(true);
        }
      });

      // Get user account type and set booking behavior
      firebase.firestore().collection('users').doc(user.uid).get()
        .then((doc) => {
          if (doc.exists) {
            // Use effective account type for premium feature checks
            if (!effectiveAccountType) {
              setBookingBehavior('popup');
            } else if (effectiveAccountType === 'free') {
              setBookingBehavior('popup');
              // Update the stored preference to popup
              firebase.firestore().collection('userPreferences').doc(user.uid)
                .set({
                  bookingBehavior: 'popup'
                }, { merge: true });
            } else {
              // For non-free users, get their preference
              firebase.firestore().collection('userPreferences').doc(user.uid).get()
                .then((prefDoc) => {
                  if (prefDoc.exists && prefDoc.data().bookingBehavior) {
                    setBookingBehavior(prefDoc.data().bookingBehavior);
                  } else {
                    setBookingBehavior('popup'); // Default to popup if no preference
                  }
                });
            }
          }
        });
    } else {
      // For non-logged in users, default to popup
      setBookingBehavior('popup');
    }
  
    // The rest of your existing useEffect code
    const timer = setTimeout(() => {
      handleResize();
      setLoading(false);
    }, 50);
  
    window.addEventListener('resize', handleResize);
  
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
    };
  }, [data.siteId, effectiveAccountType]);
  

    const toggleFavorite = (event) => {
        event.stopPropagation();
        const user = firebase.auth().currentUser;
        if (user) {
            if (isFavorite) {
                // Remove from favorites
                const newFavorites = prevFavorites.filter(favId => favId !== parseInt(data.siteId));
                const updatedFavorites = setFavorites(newFavorites);
                setIsFavorite(updatedFavorites.includes(parseInt(data.siteId)));
            } else {
                // Add to favorites
                const newFavorites = [...prevFavorites, parseInt(data.siteId)];
                const updatedFavorites = setFavorites(newFavorites);
                setIsFavorite(updatedFavorites.includes(parseInt(data.siteId)));
            }
        } else {
            // Show the login popup if the user is not logged in
            const rect = starRef.current.getBoundingClientRect();
            const position = {
                top: rect.bottom + window.scrollY,
                left: rect.left + window.scrollX
            };
            setShowLoginPopup(position);
        }
    };

    const LoginPopup = ({ position, onClose }) => (
        <div style={{
            position: 'absolute', 
            top: position.top, 
            left: position.left, 
            backgroundColor: 'var(--cc-surface)',
            padding: '20px', 
            borderRadius: '5px', 
            boxShadow: 'var(--cc-shadow)',
            zIndex: 1000
        }}>
            <Typography variant="h6" color="textSecondary">{isFrench ? "Connexion Requise" : "Login Required"}</Typography>
            <Typography variant="body1" style={{ marginBottom: '20px', marginTop:'10px' }} color="textSecondary">{isFrench ? "Vous avez besoin d'un compte pour ajouter aux favoris." : "You need an account to add to favorites."}</Typography>
            <Button style={{ marginRight: '10px' }} onClick={onClose}>{isFrench ? "Fermer" : "Close"}</Button>
            <Button variant="contained" color="primary" onClick={() => {
                navigate('/login');
                onClose();
            }}>{isFrench ? "Connexion / Inscription" : "Login / Signup"}</Button>
            
        </div>
    );

  const starRef = useRef(null);

  const handleTabChange = (event, newDate) => {
    logEvent(analytics, 'table_reservation_date_change', {
      date: newDate,
    });
    setSelectedDate(newDate);
    onDateChange && onDateChange(data.siteId, newDate);
  };

  const handleCalendarAction = (type) => {
    if (!selectedBookingDetails) return;

    if (!canAddBookingsToCalendar(currentUser)) {
      setShowCalendarAccountPrompt(true);
      logEvent(analytics, 'calendar_account_prompt', { type });
      return;
    }

    const { facility, date, time } = selectedBookingDetails;
    const [year, month, day] = date.split('-');
    const [hour, minute] = time.split(':');
    
    // Create start and end times (1 hour duration)
    const startTime = new Date(year, month - 1, day, parseInt(hour), parseInt(minute));
    const endTime = new Date(startTime.getTime() + 60 * 60 * 1000); // Add 1 hour

    // Format the dates for calendar URLs
    const formatDate = (date) => {
      return date.toISOString().replace(/-|:|\.\d+/g, '');
    };

    // Extract court number if it exists
    const courtNumber = facility.match(/\d+/)?.[0] || '';
    
    // Create title based on language
    const title = isFrench
      ? encodeURIComponent(`Tennis au ${data.siteName}${courtNumber ? ` - Terrain ${courtNumber}` : ''}`)
      : encodeURIComponent(`Tennis at ${data.siteName}${courtNumber ? ` - Court ${courtNumber}` : ''}`);

    const location = encodeURIComponent(data.siteAddress);
    const details = isFrench
      ? encodeURIComponent(`Terrain: ${facility}\nAdresse: ${data.siteAddress}\nTéléphone: ${data.sitePhone}`)
      : encodeURIComponent(`Court: ${facility}\nAddress: ${data.siteAddress}\nPhone: ${data.sitePhone}`);

    if (type === 'google') {
      const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${formatDate(startTime)}/${formatDate(endTime)}&details=${details}&location=${location}`;
      window.open(url, '_blank');
    } else if (type === 'apple') {
      const url = `data:text/calendar;charset=utf-8,BEGIN:VCALENDAR
VERSION:2.0
BEGIN:VEVENT
URL:${location}
DTSTART:${formatDate(startTime)}
DTEND:${formatDate(endTime)}
SUMMARY:${title}
DESCRIPTION:${details}
LOCATION:${location}
END:VEVENT
END:VCALENDAR`;
      
      const link = document.createElement('a');
      link.href = url;
      link.download = 'tennis_booking.ics';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    logEvent(analytics, 'calendar_add', {
      type: type,
      facility: facility,
      date: date,
      time: time
    });
  };

  const handleBookClick = (event, link) => {
    event.preventDefault();
    event.stopPropagation();

    if (!quickLinks) {
      console.error('Quick links not loaded yet');
      return;
    }

    if (link.isPredictive && !userIsPremium) {
      logEvent(analytics, 'predictive_upgrade_prompt', {
        facility: link.facility,
        time: link.time,
        date: link.date,
        source: 'table'
      });
      setShowPredictiveUpgrade(true);
      return;
    }

    const actualBookingLink = link.isPredictive
      ? predictiveLinks?.[link.bookingLinkKey]?.url
      : quickLinks[link.bookingLinkKey];
    const actualSearchLink = quickLinks[link.searchLinkKey]
      || (link.isPredictive ? predictiveLinks?.[link.searchLinkKey]?.url : null);

    if (!actualBookingLink || !actualSearchLink) {
      console.error('Links not found');
      return;
    }

    // Set the booking details for the calendar modal
    setSelectedBookingDetails({
      facility: link.facility,
      date: link.date,
      time: link.time,
      searchLink: actualSearchLink,
      bookingLink: actualBookingLink,
      isPredictive: !!link.isPredictive
    });

    logEvent(analytics, 'table_reservation_click', {
      facility: link.facility,
      time: link.time,
      date: link.date,
      link: actualBookingLink,
      behavior: bookingBehavior,
      predictive: !!link.isPredictive
    });

    // Predictive slots always require explicit acknowledgement via the dialog,
    // regardless of the user's preferred booking behavior.
    if (link.isPredictive) {
      setShowCalendarModal(true);
      return;
    }

    // The effective account type is already resolved by App. Avoid another
    // Firestore read here so a newly registered user can use the booking and
    // calendar dialog before their profile document has finished propagating.
    if (currentUser) {
      setShowCalendarModal(true);
      if (userIsPremium) {
        if (bookingBehavior === 'direct') {
          handleDirectBooking(actualBookingLink);
        } else if (bookingBehavior === 'search') {
          window.open(actualSearchLink, '_blank');
        }
      }
    } else {
      // For non-logged in users, always show popup
      setShowCalendarModal(true);
    }
  };

  const handleDirectBooking = (bookingLink) => {
    if (!bookingLink) {
      console.error('Booking link not found');
      return;
    }

    if (!currentUser) {
      if (directBookingsLeft > 0) {
        const newCount = directBookingsLeft - 1;
        localStorage.setItem('directBookingsLeft', newCount.toString());
        setDirectBookingsLeft(newCount);
        window.open(bookingLink, '_blank');
      } else {
        navigate('/login');
        setShowCalendarModal(false);
      }
    } else {
      if (weeklyBookingsLeft === null) {
        // User has standard plan - unlimited bookings
        window.open(bookingLink, '_blank');
      } else if (weeklyBookingsLeft > 0) {
        const newCount = weeklyBookingsLeft - 1;
        localStorage.setItem('weeklyBookingsLeft', newCount.toString());
        setWeeklyBookingsLeft(newCount);
        window.open(bookingLink, '_blank');
      } else {
        // Show upgrade message or redirect to profile
        navigate('/profile');
        setShowCalendarModal(false);
      }
    }
  };

  const getSlotPresentation = (facility, time, date) => {
    const { availability } = facility;
    const slots = availability && availability[date];
    const slot = slots && slots.find(s => s.startTime === time);

    if (!slot) {
      return { status: 'unavailable' };
    }

    const searchLinkKey = slot.searchLinkKey
      || facility.searchLinks?.[date]?.[time]
      || facility.searchLinks?.[date]?.['all'];

    const normalUrl = quickLinks?.[slot.linkKey];
    const predictive = predictiveLinks?.[slot.linkKey];

    let mode = null;
    if (normalUrl) {
      mode = 'normal';
    } else if (
      slot.isPredictive
      && currentUser
      && predictive
      && isPredictiveSlotVisible(slot)
    ) {
      // Show predictive slots to any logged-in user; free users get an upgrade
      // prompt when they click instead of the actual booking link.
      mode = 'predictive';
    }

    if (!mode) {
      return { status: 'unavailable' };
    }

    return {
      status: 'available',
      isPredictive: mode === 'predictive',
      linkData: {
        facility: facility.facilityName,
        time,
        date,
        searchLinkKey,
        bookingLinkKey: slot.linkKey,
        isPredictive: mode === 'predictive'
      }
    };
  };

  const extractNumber = (name) => {
    const matches = name.match(/\d+/);
    return matches ? parseInt(matches[0]) : 0;
  };

  // add a function to extract tennis or pickleball from the name or any similar name. Return T if tennis, P if pickleball, TC if both are in the name, and empty string if neither
  const extractSport = (name) => {
    name = name.toLowerCase();
  
    if (name.includes("tennis") && name.includes("pickleball")) {
      return "TC"; // Both tennis and pickleball are in the name
    } else if (name.includes("tennis")) {
      return "T"; // Tennis is in the name
    } else if (name.includes("pickleball")) {
      return "P"; // Pickleball is in the name
    } else {
      return ""; // Neither tennis nor pickleball is in the name
    }
  };

  const sortedTableStats = data.tableStats ? [...data.tableStats].sort((a, b) => {
    const nameA = a.facilityName.toUpperCase();
    const nameB = b.facilityName.toUpperCase();

    const numberA = extractNumber(nameA);
    const numberB = extractNumber(nameB);

    if (numberA !== numberB) {
      return numberA - numberB;
    }

    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }

    return 0;
  }) : [];

  // i want the sorted Dates to be in order of the dates, but using all of tableStats entries since some will have dates that others don't
  const availabilityDates = []; 
  // extract keys from data.tableStats and add them to availabilityDates if they aren't already there
  sortedTableStats.forEach((facility) => {
    Object.keys(facility.availability).forEach((date) => {
      if (!availabilityDates.includes(date)) {
        availabilityDates.push(date);
      }
    });
  });

  const sortedDates = availabilityDates.sort();

  const toggleExpanded = () => {
    logEvent(analytics, 'table_reservation_expand', {
      facility: data.siteName,
      expanded: !isExpanded
    });
    onExpansionChange(!isExpanded);
  };

  useEffect(() => {
    const user = firebase.auth().currentUser;
    if (user) {
      firebase.firestore().collection('userPreferences').doc(user.uid).get()
        .then((doc) => {
          if (doc.exists) {
            const data = doc.data();
            setBookingBehavior(data.bookingBehavior || 'popup');
          }
        });
      setDirectBookingsLeft(null); // Reset for logged in users
    } else {
      // For non-logged in users, get remaining bookings from localStorage
      const remaining = localStorage.getItem('directBookingsLeft');
      setDirectBookingsLeft(remaining === null ? 3 : parseInt(remaining));
    }
  }, [currentUser]);

  useEffect(() => {
    const user = firebase.auth().currentUser;
    if (user) {
      // For logged in users, check weekly bookings from localStorage
      const currentWeek = getWeekNumber(new Date());
      const storedWeek = localStorage.getItem('bookingWeek');
      const storedCount = localStorage.getItem('weeklyBookingsLeft');
      
      if (storedWeek !== currentWeek.toString()) {
        // Reset count for new week
        localStorage.setItem('bookingWeek', currentWeek);
        localStorage.setItem('weeklyBookingsLeft', '3');
        setWeeklyBookingsLeft(3);
      } else {
        setWeeklyBookingsLeft(storedCount === null ? 3 : parseInt(storedCount));
      }

      // Check user's subscription status
      firebase.firestore().collection('users').doc(user.uid).get()
          .then((doc) => {
              if (doc.exists) {
                  const userData = doc.data();
                  if (userData.accountType === 'standard') {
                      setWeeklyBookingsLeft(null); // Unlimited bookings for standard users
                  }
              }
          });
    } else {
      // For non-logged in users, check monthly bookings from localStorage
      const currentMonth = new Date().getMonth();
      const storedMonth = localStorage.getItem('bookingMonth');
      const storedCount = localStorage.getItem('directBookingsLeft');
      
      if (storedMonth !== currentMonth.toString()) {
          // Reset count for new month
          localStorage.setItem('bookingMonth', currentMonth);
          localStorage.setItem('directBookingsLeft', '3');
          setDirectBookingsLeft(3);
      } else {
          setDirectBookingsLeft(storedCount === null ? 3 : parseInt(storedCount));
      }
    }
  }, [currentUser]);

  const getWeekNumber = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  };

  useEffect(() => {
    if (styleguideLinks) {
      setQuickLinks(styleguideLinks.quickLinks || {});
      setQuickLinksLoaded(true);
      return undefined;
    }

    setQuickLinksLoaded(false);
    // Subscribe to quickLinks document
    const unsubscribe = firebase.firestore()
      .collection('overview')
      .doc('quickLinks')
      .onSnapshot((doc) => {
        if (doc.exists) {
          setQuickLinks(doc.data().quickLinks || {});
        } else {
          setQuickLinks({});
        }
        setQuickLinksLoaded(true);
      }, (error) => {
        console.error('Error loading booking links:', error);
        setQuickLinks({});
        setQuickLinksLoaded(true);
      });

    return () => unsubscribe();
  }, [styleguideLinks]);

  useEffect(() => {
    if (styleguideLinks) {
      return undefined;
    }

    // Subscribe to predictiveLinks document
    const unsubscribe = firebase.firestore()
      .collection('overview')
      .doc('predictiveLinks')
      .onSnapshot((doc) => {
        if (doc.exists) {
          setPredictiveLinks(doc.data().predictiveLinks || {});
        } else {
          setPredictiveLinks({});
        }
      });

    return () => unsubscribe();
  }, [styleguideLinks]);

  // Add effect to sync with parent's selected date
  useEffect(() => {
    if (parentSelectedDate && parentSelectedDate !== selectedDate) {
      setSelectedDate(parentSelectedDate);
    }
  }, [parentSelectedDate]);

  const handleCallClick = () => {
    if (data.sitePhone) {
      logEvent(analytics, 'phone_call', {
        facility: data.siteName,
        phone: data.sitePhone
      });
      window.location.href = `tel:${data.sitePhone.replace(/\D/g, '')}`;
    }
  };

  const formatFacilityLabel = (name, index) => {
    const number = extractNumber(name);
    const sport = extractSport(name);

    const shortSport = sport === 'TC' ? 'M' : (sport || 'T');
    return `${shortSport}${number || index + 1}`;
  };

  const isPastTime = (time) => (
    getTorontoNowString().slice(0, 16) > `${selectedDate}T${time}`
  );

  const selectedDateLabel = selectedDate
    ? new Intl.DateTimeFormat(isFrench ? 'fr-CA' : 'en-CA', {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      }).format(new Date(`${selectedDate}T12:00:00`))
    : '';

  const selectedOpenCount = !loading && data.tableStats
    ? data.tableStats.reduce((total, stat) => {
        const slots = stat.availability?.[selectedDate];
        return total + (Array.isArray(slots) ? slots.length : 0);
      }, 0)
    : 0;

  return (
    <div className="court-result-card">
      {showLoginPopup && <LoginPopup position={showLoginPopup} onClose={() => setShowLoginPopup(null)} />}
      <span
        className={isExpanded ? 'court-card-toggle is-expanded' : 'court-card-toggle'}
        style={{ cursor: 'pointer' }}
        onClick={toggleExpanded}
      >
        <Typography
          variant={isMobile ? "h6" : "h4"}
          component="h2"
          className="court-card-heading"
        >
          {!loading && (
            <Tooltip title={isFrench ? "Ajouter aux favoris" : "Add to favorites"}>
              <IconButton
                ref={starRef}
                onClick={toggleFavorite}
                size={isMobile ? "small" : "medium"}
                className="court-card-favorite"
              >
                {isFavorite
                  ? <StarIcon sx={{ color: 'var(--cc-star)', fontSize: isMobile ? 20 : 24 }} />
                  : <StarBorderIcon sx={{ color: 'var(--cc-text-soft)', fontSize: isMobile ? 20 : 24 }} />}
              </IconButton>
            </Tooltip>
          )}
          <span className="court-card-title">{loading ? "" : data.siteName}</span>
          {!loading && data.distanceInKm && (
            <small className="court-card-distance court-card-distance-heading">~{data.distanceInKm} km</small>
          )}
          {!loading && (
            isExpanded
              ? <ExpandLessIcon className="court-card-chevron" sx={{ fontSize: isMobile ? 20 : 24 }} />
              : <ExpandMoreIcon className="court-card-chevron" sx={{ fontSize: isMobile ? 20 : 24 }} />
          )}
        </Typography>
      </span>
      {!loading && (
        <div className="court-card-summary">
          <div className="court-card-location">
            <span>{data.siteAddress}</span>
            {data.siteGoogleMapsLink && (
              <Link
                href={data.siteGoogleMapsLink}
                target="_blank"
                rel="noreferrer"
                onClick={(event) => event.stopPropagation()}
              >
                {isFrench ? 'Carte ↗' : 'Map ↗'}
              </Link>
            )}
            {data.sitePhone && <span className="court-card-phone">{data.sitePhone}</span>}
          </div>
          <div className="court-card-meta">
          <span className={selectedOpenCount > 0 ? 'availability-pill' : 'availability-pill is-empty'}>
            {selectedOpenCount > 0
              ? (isFrench ? `${selectedOpenCount} libres` : `${selectedOpenCount} open`)
              : (isFrench ? 'Aucun créneau' : 'No open slots')}
          </span>
            {data.distanceInKm && (
              <small className="court-card-distance court-card-distance-summary">~{data.distanceInKm} km</small>
            )}
          </div>
        </div>
      )}
      <Collapse in={isExpanded}>
        <div className="court-card-expanded">
          {!loading && (
            <StyledTabs
              value={selectedDate}
              onChange={handleTabChange}
              centered
              variant="fullWidth"
              style={{ marginTop: '20px', marginBottom: '20px' }}
            >
              {sortedDates.map(date => (
                <Tab 
                  key={date} 
                  label={formatDate(date, isFrench, isMobile)} 
                  value={date} 
                  style={{ 
                    minWidth: '0', 
                    fontSize: isMobile ? '0.7em' : '1em',
                    padding: isMobile ? '6px 8px' : '12px 16px'
                  }} 
                />
              ))}
            </StyledTabs>
          )}

          {!loading && (
            <TableContainer
              className="court-table-container"
              component={Paper}
              role="region"
              tabIndex={0}
              aria-label={isFrench
                ? `Grille défilante — ${data.siteName}`
                : `Scrollable grid — ${data.siteName}`}
            >
              {!quickLinksLoaded && sortedTableStats.length > 0 ? (
                <div
                  className="availability-grid-loading-status"
                  role="status"
                  aria-live="polite"
                  aria-label={translations[isFrench ? 'fr' : 'en'].availabilityLoading}
                >
                  <span className="visually-hidden">
                    {translations[isFrench ? 'fr' : 'en'].availabilityLoading}
                  </span>
                  <Table
                    stickyHeader
                    className="availability-grid availability-grid--loading"
                    aria-hidden="true"
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell className="stickyColumn">{isFrench ? "Heure" : "Time"}</TableCell>
                        {sortedTableStats.map((facility, index) => (
                          <TableCell
                            key={facility.facilityId}
                            className="stickyHeader court-facility-heading"
                            title={facility.facilityName}
                          >
                            {formatFacilityLabel(facility.facilityName, index)}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {times.map(time => (
                        <TableRow key={time} className="availability-grid-row">
                          <TableCell className="stickyColumn availability-time">
                            {time}
                          </TableCell>
                          {sortedTableStats.map(facility => (
                            <TableCell key={facility.facilityId} className="availability-grid-cell">
                              <span className="availability-slot availability-slot--loading" />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : sortedTableStats.length === 0 ? (
                <Typography 
                  variant={isMobile ? "body2" : "body1"} 
                  sx={{ 
                    padding: 3,
                    textAlign: 'center',
                    color: 'textSecondary',
                    opacity: 0.9,
                    fontSize: isMobile ? '0.85rem' : '1rem'
                  }}
                >
                  {isFrench ? "Aucun terrain disponible" : "No courts available"}
                </Typography>
              ) : (
                <Table
                  stickyHeader
                  className="availability-grid"
                  aria-label={isFrench
                    ? `Disponibilités — ${data.siteName}`
                    : `Availability — ${data.siteName}`}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell className="stickyColumn">{isFrench ? "Heure" : "Time"}</TableCell>
                      {sortedTableStats.map((t, index) => (
                        <TableCell
                          key={t.facilityId}
                          className="stickyHeader court-facility-heading"
                          title={t.facilityName}
                          aria-label={t.facilityName}
                        >
                          {formatFacilityLabel(t.facilityName, index)}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {times.map(time => (
                      <TableRow
                        key={time}
                        className="availability-grid-row"
                      >
                        <TableCell
                          className={`stickyColumn availability-time${isPastTime(time) ? ' availability-time--past' : ''}`}
                        >
                          {time}
                        </TableCell>
                        {sortedTableStats.map(facility => {
                          const slot = getSlotPresentation(facility, time, selectedDate);
                          const isAvailable = slot.status === 'available';
                          const bookingLabel = isFrench
                            ? `Réserver ${facility.facilityName} à ${time}, ${selectedDateLabel}`
                            : `Book ${facility.facilityName} at ${time}, ${selectedDateLabel}`;
                          const unavailableLabel = isFrench
                            ? `${facility.facilityName}, ${time} — non disponible`
                            : `${facility.facilityName}, ${time} — unavailable`;

                          return (
                            <TableCell
                              key={facility.facilityId}
                              className="availability-grid-cell"
                              aria-label={isAvailable ? undefined : unavailableLabel}
                            >
                              {isAvailable ? (
                                <button
                                  type="button"
                                  className={`availability-slot availability-slot--available${slot.isPredictive ? ' availability-slot--predictive' : ''}`}
                                  onClick={(event) => handleBookClick(event, slot.linkData)}
                                  aria-label={bookingLabel}
                                  title={slot.isPredictive
                                    ? translations[isFrench ? 'fr' : 'en'].predictiveTitle
                                    : bookingLabel}
                                >
                                  <span className="visually-hidden">{bookingLabel}</span>
                                  {slot.isPredictive && (
                                    <span className="availability-slot__premium" aria-hidden="true">★</span>
                                  )}
                                </button>
                              ) : (
                                <span
                                  className="availability-slot availability-slot--unavailable"
                                  aria-hidden="true"
                                />
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TableContainer>
          )}
        </div>
      </Collapse>

      <Dialog 
        open={showCalendarModal} 
        onClose={() => setShowCalendarModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <StyledDialogTitle
          sx={{
            fontSize: { xs: '1rem', sm: '1.5rem' }
          }}
        >
          {isFrench ? "Réservation" : "Booking"}
          <IconButton
            aria-label="close"
            onClick={() => setShowCalendarModal(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8
            }}
          >
            <CloseIcon />
          </IconButton>
        </StyledDialogTitle>
        <StyledDialogContent>
          {selectedBookingDetails && (
            <>
              {selectedBookingDetails.isPredictive && (
                <Box
                  sx={{
                    mt: 1,
                    mb: 2,
                    p: 1.5,
                    borderRadius: '10px',
                    border: '1px solid var(--cc-premium)',
                    backgroundColor: 'var(--cc-premium-soft)'
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, color: 'var(--cc-premium)', mb: 0.5 }}
                  >
                    ★ {translations[isFrench ? 'fr' : 'en'].predictiveTitle}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                    {translations[isFrench ? 'fr' : 'en'].predictiveNotice}
                  </Typography>
                </Box>
              )}
              <Typography
                variant="h5"
                sx={{
                  mt: { xs: 1.5, sm: 1.5 },
                  mb: { xs: 2, sm: 2.5 },
                  fontWeight: 600,
                  color: 'primary.main',
                  textAlign: 'center',
                  fontSize: { xs: '1.2rem', sm: '1.5rem' }
                }}
              >
                {data.siteName}
              </Typography>

              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: { xs: 1.5, sm: 2.5 }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 } }}>
                  <CourtIcon color="action" sx={{ fontSize: { xs: 24, sm: 28 } }} />
                  <Typography variant="body1" sx={{ 
                    fontSize: { xs: '0.95rem', sm: '1.1rem' }
                  }}>
                    {selectedBookingDetails.facility}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 } }}>
                  <PlaceIcon color="action" sx={{ fontSize: { xs: 24, sm: 28 } }} />
                  <Typography variant="body1" sx={{ 
                    fontSize: { xs: '0.90rem', sm: '1.1rem' }
                  }}>
                    {data.siteAddress}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 } }}>
                  <EventIcon color="action" sx={{ fontSize: { xs: 24, sm: 28 } }} />
                  <Typography variant="body1" sx={{ 
                    fontSize: { xs: '0.95rem', sm: '1.1rem' }
                  }}>
                    {formatFullDate(selectedBookingDetails.date, isFrench)}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 } }}>
                  <ScheduleIcon color="action" sx={{ fontSize: { xs: 24, sm: 28 } }} />
                  <Typography variant="body1" sx={{ 
                    fontSize: { xs: '0.95rem', sm: '1.1rem' }
                  }}>
                    {selectedBookingDetails.time}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ mt: { xs: 2.5, sm: 4 }, mb: { xs: 2, sm: 3 } }}>
                {(bookingBehavior === 'popup' || bookingBehavior === 'search') && (
                  <GreenOutlinedButton
                    fullWidth
                    onClick={() => window.open(selectedBookingDetails.searchLink, '_blank')}
                    sx={{ 
                      mb: { xs: 1.5, sm: 2 },
                      height: { xs: '40px', sm: '44px' },
                      fontSize: { xs: '0.9rem', sm: '0.95rem' },
                      borderWidth: '1.5px',
                      '&:hover': {
                        borderWidth: '1.5px'
                      }
                    }}
                    startIcon={<CallMadeIcon />}
                  >
                    {isFrench ? "Voir les résultats de recherche" : "View Search Results"}
                  </GreenOutlinedButton>
                )}

                {(bookingBehavior === 'popup' || bookingBehavior === 'direct') && (
                  <GreenContainedButton
                    fullWidth
                    onClick={() => handleDirectBooking(selectedBookingDetails.bookingLink)}
                    disabled={(!currentUser && directBookingsLeft === 0) || (currentUser && weeklyBookingsLeft === 0)}
                    sx={{ 
                      mb: { xs: 1.5, sm: 2 },
                      height: { xs: '40px', sm: '44px' },
                      boxShadow: 1,
                      '&:hover': {
                        boxShadow: 2
                      }
                    }}
                    startIcon={<OpenInNewIcon />}
                  >
                    {isFrench ? "Réserver directement" : "Book Directly"}
                  </GreenContainedButton>
                )}

                <BlueOutlinedButton
                  fullWidth
                  onClick={handleCallClick}
                  sx={{ 
                    mb: { xs: 1.5, sm: 2 },
                    height: { xs: '40px', sm: '44px' },
                    fontSize: { xs: '0.9rem', sm: '0.95rem' },
                    borderWidth: '1.5px',
                    '&:hover': {
                      borderWidth: '1.5px'
                    }
                  }}
                  startIcon={<PhoneIcon />}
                >
                  {isFrench ? "Appeler pour réserver" : "Call to Book"}
                </BlueOutlinedButton>

                {!currentUser && directBookingsLeft !== null && (
                  <Typography 
                    variant="body2" 
                    color="textSecondary" 
                    sx={{ 
                      mt: { xs: 1.5, sm: 2 }, 
                      textAlign: 'center',
                      fontSize: { xs: '0.8rem', sm: '0.875rem' }
                    }}
                  >
                    {directBookingsLeft} {isFrench 
                      ? translations.fr.bookingsLeft(directBookingsLeft)
                      : translations.en.bookingsLeft}
                    <br />
                    <Link
                      component="button"
                      onClick={() => {
                        navigate('/login');
                        setShowCalendarModal(false);
                      }}
                      sx={{ 
                        textDecoration: 'none',
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      {translations[isFrench ? 'fr' : 'en'].bookingLimitUnauth}
                    </Link>
                    {' '}
                    {translations[isFrench ? 'fr' : 'en'].bookingLimitUnauthDesc}
                  </Typography>
                )}

                {currentUser && weeklyBookingsLeft !== null && (
                  <Typography 
                    variant="body2" 
                    color="textSecondary" 
                    sx={{ 
                      mt: { xs: 1.5, sm: 2 }, 
                      textAlign: 'center',
                      fontSize: { xs: '0.8rem', sm: '0.875rem' }
                    }}
                  >
                    {weeklyBookingsLeft} {isFrench 
                      ? translations.fr.bookingsLeftWeek(weeklyBookingsLeft)
                      : translations.en.bookingsLeftWeek}
                    <br />
                    <Link
                      component="button"
                      onClick={() => {
                        navigate('/profile');
                        setShowCalendarModal(false);
                      }}
                      sx={{ 
                        textDecoration: 'none',
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      {translations[isFrench ? 'fr' : 'en'].bookingLimitAuth}
                    </Link>
                  </Typography>
                )}
              </Box>

              <Divider sx={{ mb: { xs: 2, sm: 3 } }} />

              <Box sx={{ position: 'relative' }}>
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    mb: { xs: 1.5, sm: 2 }, 
                    textAlign: 'center', 
                    color: 'text.secondary',
                    fontSize: { xs: '0.9rem', sm: '1rem' }
                  }}
                >
                  {isFrench ? "Ajouter au calendrier" : "Add to Calendar"}
                </Typography>

                <Box>
                  <CalendarButton
                    variant="outlined"
                    onClick={() => handleCalendarAction('google')}
                    startIcon={<GoogleIcon />}
                    sx={{ 
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                      height: { xs: '36px', sm: '40px' },
                      mb: { xs: 1, sm: 1.5 }
                    }}
                  >
                    {isFrench ? "Ajouter à Google Calendar" : "Add to Google Calendar"}
                  </CalendarButton>
                  <CalendarButton
                    variant="outlined"
                    onClick={() => handleCalendarAction('apple')}
                    startIcon={<AppleIcon />}
                    sx={{ 
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                      height: { xs: '36px', sm: '40px' }
                    }}
                  >
                    {isFrench ? "Ajouter à Apple Calendar" : "Add to Apple Calendar"}
                  </CalendarButton>
                </Box>

                {!currentUser && (
                  <Typography 
                    variant="body2" 
                    color="textSecondary" 
                    sx={{ 
                      mt: { xs: 1.5, sm: 2 }, 
                      textAlign: 'center',
                      fontSize: { xs: '0.8rem', sm: '0.875rem' }
                    }}
                  >
                    <Link
                      component="button"
                      onClick={() => setShowCalendarAccountPrompt(true)}
                      sx={{ 
                        textDecoration: 'none',
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      {translations[isFrench ? 'fr' : 'en'].calendarAccountText}
                    </Link>
                  </Typography>
                )}
              </Box>
            </>
          )}
        </StyledDialogContent>
      </Dialog>

      <Dialog
        open={showCalendarAccountPrompt}
        onClose={() => setShowCalendarAccountPrompt(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {translations[isFrench ? 'fr' : 'en'].calendarAccountTitle}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" color="text.secondary">
            {translations[isFrench ? 'fr' : 'en'].calendarAccountBody}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap' }}>
          <Button onClick={() => setShowCalendarAccountPrompt(false)}>
            {translations[isFrench ? 'fr' : 'en'].close}
          </Button>
          <Button
            onClick={() => {
              setShowCalendarAccountPrompt(false);
              setShowCalendarModal(false);
              navigate('/login');
            }}
          >
            {translations[isFrench ? 'fr' : 'en'].signIn}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setShowCalendarAccountPrompt(false);
              setShowCalendarModal(false);
              navigate('/login', { state: { mode: 'signup' } });
            }}
          >
            {translations[isFrench ? 'fr' : 'en'].createAccount}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showPredictiveUpgrade}
        onClose={() => setShowPredictiveUpgrade(false)}
        maxWidth="xs"
        fullWidth
      >
        <StyledDialogTitle sx={{ color: 'var(--cc-premium)', fontWeight: 700 }}>
          ★ {translations[isFrench ? 'fr' : 'en'].predictiveUpgradeTitle}
          <IconButton
            aria-label="close"
            onClick={() => setShowPredictiveUpgrade(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </StyledDialogTitle>
        <StyledDialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, mt: 1 }}>
            {translations[isFrench ? 'fr' : 'en'].predictiveUpgradeBody}
          </Typography>
          <Button
            fullWidth
            variant="contained"
            onClick={() => {
              setShowPredictiveUpgrade(false);
              navigate('/profile');
            }}
            sx={{
              backgroundColor: 'var(--cc-premium)',
              textTransform: 'none',
              fontWeight: 700,
              '&:hover': { backgroundColor: 'var(--cc-premium)' }
            }}
          >
            {translations[isFrench ? 'fr' : 'en'].upgrade}
          </Button>
        </StyledDialogContent>
      </Dialog>
    </div>
  );
};

export default CourtAvailabilityTable;
